import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, ImagePlus, RotateCw, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FotoBeeld } from "./FotoBeeld";
import { fotoTekst, verklein } from "./bezoekUtils";

const MAX = 8;
const knop = "min-h-[44px] inline-flex items-center justify-center gap-2 px-4 rounded-md text-sm font-semibold transition-colors disabled:opacity-40";

// Eén foto in de wachtrij: blijft zichtbaar tot hij geüpload is; mislukt → "Opnieuw".
const Wachtend = ({ w, onOpnieuw, onWeg }) => (
  <div data-testid={`foto-wacht-${w.tid}`} className="relative aspect-square rounded-md overflow-hidden border border-zinc-200 bg-zinc-100">
    <img src={w.url} alt="Foto die wordt geüpload" className="h-full w-full object-cover opacity-60" />
    {w.status === "bezig" ? (
      <div className="absolute inset-x-0 bottom-0 h-1.5 bg-zinc-200"><div className="h-full bg-blue-600 transition-[width]" style={{ width: `${w.pct}%` }} /></div>
    ) : (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-white/80 p-1 text-center">
        <span data-testid={`foto-mislukt-${w.tid}`} className="text-[11px] font-semibold text-rose-700">Upload mislukt</span>
        <div className="flex gap-1">
          <button type="button" data-testid={`foto-opnieuw-${w.tid}`} onClick={() => onOpnieuw(w)} className="min-h-[44px] px-2 rounded bg-zinc-950 text-white text-xs font-semibold inline-flex items-center gap-1"><RotateCw className="h-3 w-3" />Opnieuw</button>
          <button type="button" aria-label="Weghalen" onClick={() => onWeg(w)} className="min-h-[44px] min-w-[44px] rounded border border-zinc-300 bg-white text-zinc-500 inline-flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
      </div>
    )}
  </div>
);

export const Fotos = ({ bezoek, magBewerken, onBezoek }) => {
  const [wacht, setWacht] = useState([]);
  const [groot, setGroot] = useState(null);
  const [bezigWeg, setBezigWeg] = useState(false);
  const camRef = useRef(null);
  const kiesRef = useRef(null);
  const urls = useRef(new Set());
  const levend = useRef(true);
  useEffect(() => { levend.current = true; return () => { levend.current = false; urls.current.forEach((u) => URL.revokeObjectURL(u)); }; }, []);
  // R1 B4: weggaan tijdens een upload (of met een mislukte foto) vraagt eerst bevestiging.
  useEffect(() => {
    if (wacht.length === 0) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [wacht.length]);

  const zet = (tid, v) => setWacht((l) => l.map((w) => (w.tid === tid ? { ...w, ...v } : w)));
  const upload = async (w) => {
    zet(w.tid, { status: "bezig", pct: 0 });
    try {
      const fd = new FormData();
      fd.append("foto", w.blob, "foto.jpg");
      const r = await api.post(`/bezoeken/${bezoek.id}/fotos`, fd, { onUploadProgress: (e) => e.total && zet(w.tid, { pct: Math.round((e.loaded / e.total) * 100) }) });
      onBezoek(r.data);
      setWacht((l) => l.filter((x) => x.tid !== w.tid));
      URL.revokeObjectURL(w.url); urls.current.delete(w.url);
    } catch (e) {
      if (!levend.current) { toast.error("Foto niet opgeslagen — open het bezoek en probeer opnieuw."); return; }
      zet(w.tid, { status: "mislukt" });
      if (e?.response?.data?.detail) toast.error(e.response.data.detail);
    }
  };
  const kies = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const ruimte = MAX - bezoek.fotos.length - wacht.length;
    if (files.length > ruimte) toast.error(`Maximaal ${MAX} foto's per bezoek.`);
    for (const f of files.slice(0, Math.max(0, ruimte))) {
      let blob;
      try { blob = await verklein(f); } catch (err) { toast.error(err.message); continue; }
      const url = URL.createObjectURL(blob);
      urls.current.add(url);
      const w = { tid: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, blob, url, status: "bezig", pct: 0 };
      setWacht((l) => [...l, w]);
      upload(w);
    }
  };
  const weg = (w) => { setWacht((l) => l.filter((x) => x.tid !== w.tid)); URL.revokeObjectURL(w.url); urls.current.delete(w.url); };
  const verwijder = async () => {
    setBezigWeg(true);
    try { const r = await api.delete(`/bezoeken/${bezoek.id}/fotos/${groot.id}`); onBezoek(r.data); setGroot(null); toast.success("Foto verwijderd"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Verwijderen mislukt"); } finally { setBezigWeg(false); }
  };

  const vol = bezoek.fotos.length + wacht.length >= MAX;
  const totaal = bezoek.fotos.length;
  return (
    <section data-testid="bezoek-fotos" className="space-y-3">
      <div className="flex items-baseline gap-2">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">Foto's</h3>
        <span data-testid="bezoek-foto-aantal" className="text-xs text-zinc-500">{fotoTekst(totaal)}</span>
      </div>
      {magBewerken && (bezoek.fotosBeschikbaar ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="foto-maken" disabled={vol} onClick={() => camRef.current?.click()} className={`${knop} bg-zinc-950 text-white hover:bg-zinc-800`}><Camera className="h-4 w-4" />Foto maken</button>
          <button type="button" data-testid="foto-kiezen" disabled={vol} onClick={() => kiesRef.current?.click()} className={`${knop} border border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50`}><ImagePlus className="h-4 w-4" />Kies foto</button>
          <input ref={camRef} data-testid="foto-input-camera" type="file" accept="image/*" capture="environment" className="hidden" onChange={kies} />
          <input ref={kiesRef} data-testid="foto-input-kies" type="file" accept="image/*" multiple className="hidden" onChange={kies} />
          {vol && <span className="text-xs text-zinc-500 self-center">Maximaal {MAX} foto's.</span>}
        </div>
      ) : <p data-testid="fotos-niet-ingesteld" className="text-sm text-zinc-500">Foto's zijn nog niet ingesteld</p>)}
      {totaal === 0 && wacht.length === 0 ? (!magBewerken && <p className="text-sm text-zinc-400">Geen foto's.</p>) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {bezoek.fotos.map((f) => (
            <button key={f.id} type="button" data-testid={`foto-thumb-${f.id}`} onClick={() => setGroot(f)} className="aspect-square rounded-md overflow-hidden border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-blue-600">
              <FotoBeeld bezoekId={bezoek.id} foto={f} className="h-full w-full" />
            </button>
          ))}
          {wacht.map((w) => <Wachtend key={w.tid} w={w} onOpnieuw={upload} onWeg={weg} />)}
        </div>
      )}
      <Dialog open={!!groot} onOpenChange={(o) => { if (!o) setGroot(null); }}>
        <DialogContent data-testid="foto-groot" className="max-w-3xl p-3">
          <DialogTitle className="sr-only">Foto</DialogTitle>
          <DialogDescription className="sr-only">De foto groot bekijken</DialogDescription>
          {groot && <FotoBeeld bezoekId={bezoek.id} foto={groot} className="w-full max-h-[75vh] object-contain bg-zinc-950 rounded" testid="foto-groot-beeld" />}
          {groot && magBewerken && (
            <button type="button" data-testid="foto-verwijderen" disabled={bezigWeg} onClick={verwijder} className={`${knop} border border-rose-200 text-rose-700 hover:bg-rose-50`}><Trash2 className="h-4 w-4" />Foto verwijderen</button>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
